import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X } from 'lucide-react';

// Global registry to track open dropdowns and close others
const dropdownRegistry = {
  openDropdownId: null,
  closeFn: null,
  
  // Called BEFORE opening a new dropdown - closes any currently open one
  closeOthers(newId) {
    if (this.openDropdownId && this.openDropdownId !== newId && this.closeFn) {
      this.closeFn();
    }
  },
  
  // Called when a dropdown opens
  register(id, closeFn) {
    this.openDropdownId = id;
    this.closeFn = closeFn;
  },
  
  // Called when a dropdown closes
  unregister(id) {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
      this.closeFn = null;
    }
  }
};

// Helper to check if we're on mobile
const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 768;

/**
 * SafariDropdown - iOS/Safari compatible dropdown component
 * 
 * Uses translate3d hardware acceleration and proper z-index management
 * to ensure dropdowns work correctly on Safari/iOS devices.
 * 
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when selection changes
 * @param {Array} options - Array of { value, label } objects (can be empty/dynamic)
 * @param {string} placeholder - Placeholder text when no selection
 * @param {string} className - Additional classes for the wrapper
 * @param {boolean} disabled - Disable the dropdown
 * @param {string} testId - data-testid attribute for testing
 * @param {boolean} searchable - Enable search filtering (auto-enabled when > 6 options)
 */
export const SafariDropdown = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select...', 
  className = '',
  disabled = false,
  testId = 'safari-dropdown',
  searchable = null // null = auto (enabled when > 6 options)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionsMaxHeight, setOptionsMaxHeight] = useState(240); // Default max-height
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsListRef = useRef(null);
  const touchHandledRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 }); // Track touch start for scroll detection
  const dropdownId = useId();

  // Check if mobile on each render (handles resize)
  // Full-screen selector works both inside and outside BottomSheets (via Portal)
  const isMobileDevice = isMobile();

  // Auto-enable search for long lists
  const showSearch = searchable !== null ? searchable : options.length > 6;

  // Filter options based on search query
  const filteredOptions = searchQuery.trim() 
    ? options.filter(opt => 
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Find selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;

  // Close function for registry
  const closeDropdown = () => setIsOpen(false);
  
  // Calculate available height for options list based on visualViewport (handles keyboard)
  const calculateOptionsHeight = useCallback(() => {
    if (!wrapperRef.current || !isOpen) return;
    
    const wrapper = wrapperRef.current;
    const wrapperRect = wrapper.getBoundingClientRect();
    
    // Use visualViewport if available (accounts for keyboard on mobile)
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    
    // Calculate space below the dropdown trigger
    // Add buffer for search input (~52px) and bottom padding (20px)
    const searchHeight = showSearch ? 52 : 0;
    const bottomBuffer = 20;
    const triggerBottom = wrapperRect.bottom;
    
    // Available space = viewport height - trigger bottom - search - buffer
    const availableHeight = viewportHeight - triggerBottom - searchHeight - bottomBuffer;
    
    // Clamp between 120px min and 300px max
    const newMaxHeight = Math.max(120, Math.min(300, availableHeight));
    
    setOptionsMaxHeight(newMaxHeight);
  }, [isOpen, showSearch]);

  // Listen to visualViewport resize (keyboard open/close)
  useEffect(() => {
    if (!isOpen) return;
    
    // Initial calculation
    calculateOptionsHeight();
    
    // Listen for viewport changes (keyboard open/close on mobile)
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', calculateOptionsHeight);
      viewport.addEventListener('scroll', calculateOptionsHeight);
      
      return () => {
        viewport.removeEventListener('resize', calculateOptionsHeight);
        viewport.removeEventListener('scroll', calculateOptionsHeight);
      };
    }
  }, [isOpen, calculateOptionsHeight]);

  // Register/unregister from global registry when opening/closing
  useEffect(() => {
    if (isOpen) {
      dropdownRegistry.register(dropdownId, closeDropdown);
    } else {
      dropdownRegistry.unregister(dropdownId);
    }
  }, [isOpen, dropdownId]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    } else if (showSearch && searchInputRef.current) {
      // Only auto-focus on desktop - prevents keyboard popup on mobile
      const isDesktop = window.innerWidth > 768;
      if (isDesktop) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [isOpen, showSearch]);
  
  // Handle search input focus on mobile - scroll into view when keyboard opens
  const handleSearchFocus = useCallback(() => {
    if (isMobile() && wrapperRef.current) {
      // Small delay to let keyboard start opening
      setTimeout(() => {
        wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Recalculate height after scroll
        setTimeout(calculateOptionsHeight, 300);
      }, 100);
    }
  }, [calculateOptionsHeight]);

  // Force reflow to fix Safari rendering issues
  const forceReflow = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.display = 'none';
      // eslint-disable-next-line no-unused-expressions
      wrapperRef.current.offsetHeight; // Trigger reflow
      wrapperRef.current.style.display = '';
    }
  };

  const handleOpen = () => {
    if (disabled || options.length === 0) return;
    
    // On mobile, open BottomSheet instead of inline dropdown
    if (isMobileDevice) {
      setIsMobileSheetOpen(true);
      setSearchQuery(''); // Reset search when opening
      return;
    }
    
    // Close any other open dropdown FIRST (synchronous)
    if (!isOpen) {
      dropdownRegistry.closeOthers(dropdownId);
    }
    
    forceReflow();
    setIsOpen(!isOpen);
  };

  // Handle selection from mobile sheet
  const handleMobileSelect = (optionValue) => {
    onChange(optionValue);
    setIsMobileSheetOpen(false);
    setSearchQuery('');
  };

  // Touch start handler - track position to detect scrolling vs tapping
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  // Touch handler for iOS - only open if it was a tap, not a scroll
  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.time;
    
    // If finger moved more than 10px or touch lasted more than 300ms, it's likely a scroll
    const isScroll = deltaX > 10 || deltaY > 10;
    const isLongPress = deltaTime > 300;
    
    if (isScroll || isLongPress) {
      // User was scrolling or holding, don't open the dropdown
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    handleOpen();
    setTimeout(() => { touchHandledRef.current = false; }, 300);
  };

  // Click handler for desktop
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (touchHandledRef.current) return;
    handleOpen();
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleBackdropClose = () => {
    setIsOpen(false);
  };

  const hasOptions = options.length > 0;
  const isDisabledState = disabled || !hasOptions;

  return (
    <div 
      ref={wrapperRef}
      className={`safari-dropdown-wrapper relative ${isOpen ? 'z-[45]' : 'z-[10]'} ${className}`}
      style={{
        overflow: 'visible',
        WebkitTransform: 'translate3d(0,0,0)',
        transform: 'translate3d(0,0,0)',
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        onTouchStart={isDisabledState ? undefined : handleTouchStart}
        onTouchEnd={isDisabledState ? undefined : handleTouchEnd}
        disabled={isDisabledState}
        data-testid={testId}
        className={`w-full px-3 py-2.5 bg-card border border-border rounded-xl flex items-center justify-between text-left text-foreground transition-colors
          ${isDisabledState ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'}
          ${isOpen ? 'border-primary ring-1 ring-primary/20' : ''}
        `}
        style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'manipulation',
        }}
      >
        <span className={`text-sm truncate ${!value ? 'text-muted-foreground' : ''}`}>
          {!hasOptions ? 'No options available' : displayValue}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Invisible Backdrop - catches clicks to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[40]" 
          onClick={handleBackdropClose}
          onTouchEnd={(e) => { e.preventDefault(); handleBackdropClose(); }}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        />
      )}
      
      {isOpen && hasOptions && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-[45]"
          style={{
            WebkitTransform: 'translate3d(0,0,0)',
            transform: 'translate3d(0,0,0)',
            overflow: 'hidden',
          }}
        >
          {/* Search Input */}
          {showSearch && (
            <div className="sticky top-0 p-2 bg-card border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-2 text-sm bg-secondary/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  onClick={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{
                    WebkitTapHighlightColor: 'transparent',
                    fontSize: '16px', // Prevents iOS zoom on focus
                  }}
                />
              </div>
            </div>
          )}
          
          <div
            ref={optionsListRef}
            className="overflow-y-auto overscroll-contain"
            style={{
              maxHeight: `${optionsMaxHeight}px`,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between text-sm
                    ${option.value === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-secondary'}
                  `}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent', 
                    minHeight: '44px',
                    touchAction: 'manipulation',
                  }}
                >
                  <span className="truncate pr-2">{option.label}</span>
                  {option.value === value && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Mobile Full-Screen Selector - rendered via Portal to body */}
      {isMobileSheetOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex flex-col bg-card"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">{placeholder}</h2>
            <button
              onClick={() => { setIsMobileSheetOpen(false); setSearchQuery(''); }}
              className="p-2 -mr-2 text-muted-foreground hover:text-foreground rounded-full hover:bg-secondary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Search input */}
          {showSearch && (
            <div className="flex-shrink-0 px-4 py-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2.5 bg-secondary border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
              </div>
            </div>
          )}
          
          {/* Options list */}
          <div className="flex-1 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-sm text-muted-foreground text-center">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleMobileSelect(option.value)}
                  className={`w-full px-4 py-4 text-left flex items-center justify-between border-b border-border/50
                    ${option.value === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground active:bg-secondary'}
                  `}
                >
                  <span className="text-base">{option.label}</span>
                  {option.value === value && <Check className="w-5 h-5 text-primary flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SafariDropdown;
